import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface FeedbackRequest {
  id: string;
  user_id: string;
  created_at: string;
  shown_at: string | null;
  dismissed_at: string | null;
  is_completed: boolean;
}

export const useFeedbackRequest = () => {
  const [user, setUser] = useState<User | null>(null);
  const [pendingRequest, setPendingRequest] = useState<FeedbackRequest | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    // Don't show feedback dialog on public clinic pages
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/clinic/') && !currentPath.includes('/dashboard')) {
      return;
    }
    checkForPendingRequest();
  }, []);

  const checkForPendingRequest = async () => {
    // Get current user
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) return;

    setUser(currentUser);

    // Check if user is admin - admins should not see feedback dialog
    const { data: adminRole } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", currentUser.id)
      .eq("role", "admin")
      .maybeSingle();

    if (adminRole) {
      // Admin users should not be prompted for feedback
      return;
    }

    // Check if user already has a testimonial
    const { data: existingTestimonial } = await supabase
      .from("testimonials")
      .select("id")
      .eq("user_id", currentUser.id)
      .limit(1);

    if (existingTestimonial && existingTestimonial.length > 0) {
      // User already submitted a testimonial, no need to show dialog
      return;
    }

    // Check for pending feedback request that hasn't been dismissed recently (within 24h)
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    const { data: request } = await supabase
      .from("feedback_requests")
      .select("*")
      .eq("user_id", currentUser.id)
      .eq("is_completed", false)
      .order("created_at", { ascending: false })
      .limit(1);

    if (request && request.length > 0) {
      const latestRequest = request[0] as FeedbackRequest;
      
      // If dismissed recently (within 24h), don't show
      if (latestRequest.dismissed_at && latestRequest.dismissed_at > twentyFourHoursAgo) {
        return;
      }

      // Show the dialog
      setPendingRequest(latestRequest);
      setShowDialog(true);
    }
  };

  return {
    user,
    pendingRequest,
    showDialog,
    setShowDialog,
  };
};
